import React from 'react';

const ProfessionalTemplate = ({ resumeData }) => {
    return (
        <div className="bg-white w-[210mm] min-h-[297mm] shadow-2xl origin-top transform scale-[0.4] md:scale-[0.65] lg:scale-[0.85] xl:scale-100 transition-transform duration-300 p-[20mm] text-slate-800 font-sans" id="resume-preview">

            {/* Header */}
            <div className="border-b-4 border-slate-800 pb-6 mb-8 flex justify-between items-end">
                <div>
                    <h1 className="text-4xl font-bold text-slate-900 mb-2">{resumeData.personal.fullName || "Your Name"}</h1>
                    <div className="text-lg text-slate-600 font-medium">Professional Profile</div>
                </div>
                <div className="text-right text-sm text-slate-600 space-y-1">
                    {resumeData.personal.email && <div>{resumeData.personal.email}</div>}
                    {resumeData.personal.phone && <div>{resumeData.personal.phone}</div>}
                    {resumeData.personal.location && <div>{resumeData.personal.location}</div>}
                </div>
            </div>

            {/* Summary */}
            {resumeData.personal.summary && (
                <div className="mb-8 bg-slate-50 p-6 rounded-lg border-l-4 border-slate-800">
                    <p className="text-sm leading-relaxed text-slate-700 text-justify font-medium">{resumeData.personal.summary}</p>
                </div>
            )}

            {/* Dynamic Sections */}
            {resumeData.sectionOrder.map((sectionId) => {
                if (sectionId === 'experience' && resumeData.experience.length > 0) {
                    return (
                        <div key={sectionId} className="mb-8">
                            <div className="flex items-center gap-4 mb-6">
                                <h2 className="text-xl font-bold text-slate-900 uppercase tracking-tight">Experience</h2>
                                <div className="flex-1 h-px bg-slate-300"></div>
                            </div>
                            <div className="space-y-6">
                                {resumeData.experience.map((exp) => (
                                    <div key={exp.id}>
                                        <div className="flex justify-between items-center mb-2">
                                            <h3 className="font-bold text-lg text-slate-900">{exp.role}</h3>
                                            <span className="text-sm font-bold text-white bg-slate-800 px-3 py-1 rounded-full">{exp.startDate} - {exp.endDate}</span>
                                        </div>
                                        <div className="text-md font-semibold text-slate-600 mb-3">{exp.company} {exp.location && `| ${exp.location}`}</div>
                                        <p className="text-sm leading-relaxed text-slate-700 whitespace-pre-line">{exp.description}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    );
                }

                if (sectionId === 'education' && resumeData.education.length > 0) {
                    return (
                        <div key={sectionId} className="mb-8">
                            <div className="flex items-center gap-4 mb-6">
                                <h2 className="text-xl font-bold text-slate-900 uppercase tracking-tight">Education</h2>
                                <div className="flex-1 h-px bg-slate-300"></div>
                            </div>
                            <div className="grid grid-cols-2 gap-6">
                                {resumeData.education.map((edu) => (
                                    <div key={edu.id} className="bg-slate-50 p-4 rounded border border-slate-200">
                                        <h3 className="font-bold text-md text-slate-900 mb-1">{edu.school}</h3>
                                        <div className="text-sm text-slate-700 mb-2">{edu.degree}</div>
                                        <div className="text-xs font-bold text-slate-500 uppercase">{edu.year}</div>
                                        {edu.location && <div className="text-xs text-slate-400 mt-1">{edu.location}</div>}
                                    </div>
                                ))}
                            </div>
                        </div>
                    );
                }

                if (sectionId === 'skills' && resumeData.skills.length > 0) {
                    return (
                        <div key={sectionId} className="mb-8">
                            <div className="flex items-center gap-4 mb-6">
                                <h2 className="text-xl font-bold text-slate-900 uppercase tracking-tight">Skills</h2>
                                <div className="flex-1 h-px bg-slate-300"></div>
                            </div>
                            <div className="flex flex-wrap gap-3">
                                {resumeData.skills.map((skill, idx) => (
                                    <span key={idx} className="bg-white border-2 border-slate-800 text-slate-800 px-4 py-1.5 rounded font-bold text-sm shadow-sm">
                                        {skill}
                                    </span>
                                ))}
                            </div>
                        </div>
                    );
                }

                if (typeof sectionId === 'number') {
                    const section = resumeData.customSections.find(s => s.id === sectionId);
                    if (section && section.items.length > 0) {
                        return (
                            <div key={sectionId} className="mb-8">
                                <div className="flex items-center gap-4 mb-6">
                                    <h2 className="text-xl font-bold text-slate-900 uppercase tracking-tight">{section.title}</h2>
                                    <div className="flex-1 h-px bg-slate-300"></div>
                                </div>
                                <div className="space-y-6">
                                    {section.items.map((item) => (
                                        <div key={item.id}>
                                            <div className="flex justify-between items-center mb-1">
                                                <h3 className="font-bold text-lg text-slate-900">{item.title}</h3>
                                                {item.subtitle && <span className="text-sm font-bold text-slate-500">{item.subtitle}</span>}
                                            </div>
                                            {item.subtext && <div className="text-sm font-semibold text-slate-600 mb-2">{item.subtext}</div>}
                                            {item.description && <p className="text-sm leading-relaxed text-slate-700 whitespace-pre-line">{item.description}</p>}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    }
                }

                return null;
            })}
        </div>
    );
};

export default ProfessionalTemplate;
